import { changeGrowth } from "../../growth/api";
import {
  buildUrl,
  defaultFetchHeaders,
  defaultReadHeaders,
  fetchJson,
  getCsrfToken,
} from "../../../utils";
import { setCsrfToken } from "../../../user/api";
import {
  Location,
  Address,
  PhotoAsset,
  RoutePlannerLocationMetric,
  ServiceCadenceTaskReschedulePreview,
  LocationStatusImpact,
} from "../../types";
import { centsFromApi, optionalCentsFromApi } from "../../currency";
import { errorMessageFromResponse, jsonFromResponse } from "../../../apiError";
import { normalizePhotoAsset } from "../assets";
import {
  buildLocationPayload,
  normalizeLocationResponse,
  normalizeHoursPayload,
  normalizeAddressPayload,
  normalizeServiceCadencePayload,
} from "./normalize";
import { createMemoryCache } from "../../cache";
import { suppressRealtimeEvent } from "../../realtime/suppressRealtime";
import { toLocalISODate } from "../../utils";
export * from "./contacts";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const locationBase = `${backend}/location`;

function parseFilename(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (match && match[1]) return decodeURIComponent(match[1]);
  return fallback;
}

function buildLocationIdCardsUrl(ids: string[]) {
  const list = ids.filter(Boolean).join(",");
  return buildUrl(`${locationBase}/id-card/bulk`, { ids: list });
}

const jsonHeaders = async () => ({
  ...(await defaultFetchHeaders()),
  "Content-Type": "application/json",
});

const METRICS_CACHE_TTL_MS = 60_000;
const metricsCache = createMemoryCache<RoutePlannerLocationMetric[]>();
const metricsInFlight = new Map<string, Promise<RoutePlannerLocationMetric[]>>();

export const resetLocationMetricsCache = () => {
  metricsCache.clear();
  metricsInFlight.clear();
};

const getMetricsCacheKey = (params: {
  scheduledFor?: string;
  date?: string;
  start?: string;
  end?: string;
}) => {
  const scheduledFor = params.scheduledFor ?? params.date ?? "";
  const start = params.start ?? "";
  const end = params.end ?? "";
  return `${scheduledFor}|${start}|${end}`;
};

export async function fetchLocations(): Promise<Location[]> {
  const locations = await fetchJson<Location[]>(
    buildUrl(`${locationBase}/all`, {}),
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch locations"
  );
  return locations.map((loc) => normalizeLocationResponse(loc));
}

export async function fetchLocation(id: string): Promise<Location> {
  const location = await fetchJson<Location>(
    buildUrl(`${locationBase}`, { id }),
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch location"
  );
  return normalizeLocationResponse(location);
}

export async function downloadLocationIdCards(ids: string[]): Promise<{
  blob: Blob;
  filename: string;
}> {
  if (!ids.length) {
    throw new Error("No locations selected");
  }
  const res = await fetch(buildLocationIdCardsUrl(ids), {
    method: "GET",
    credentials: "include",
    headers : await defaultFetchHeaders(),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to download location IDs")
    );
  }
  const blob = await res.blob();
  const filename = parseFilename(
    res.headers.get("content-disposition"),
    "location-ids.pdf"
  );
  return { blob, filename };
}

export async function createLocation(payload: Location): Promise<Location> {
  const body = buildLocationPayload(payload, {
    includeAddress: true,
    includeMachines: true,
  });

  const created = payload.sourceGrowthLeadId
    ? await changeGrowth<Location>(`/leads/${encodeURIComponent(payload.sourceGrowthLeadId)}/convert`, "POST", { location: { ...body, operationId: payload.operationId }, ...(payload.sourceGrowthLeadStatus ? { status: payload.sourceGrowthLeadStatus } : {}) })
    : await fetchJson<Location>(
    `${locationBase}/create`,
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify({ ...body, operationId: payload.operationId }),
    },
    "Failed to create location"
  );
  const normalized = normalizeLocationResponse(created);
  return normalized;
}

export type ServiceCadenceSuggestion = {
  cadence: {
    frequency: number;
    dayOfWeek: string;
  };
  frequencySuggestion?: {
    frequency: number;
    usedHistory: boolean;
    confidence: string;
    reason: string;
    warnings: string[];
    metrics: {
      collectionCount: number;
      intervalCount: number;
      grossCents: number;
      activeDays: number;
      dailyGrossCents?: number | null;
      revenueThresholdCents?: number | null;
    };
  } | null;
  isCurrent?: boolean;
  confidence: "high" | "medium" | "low" | string;
  score: number;
  reason: string;
  warnings: string[];
  metrics: {
    sameDayLocations: number;
    sameDayMachines: number;
    dayLocationCountAfterAdd: number;
    dayMachineCountAfterAdd: number;
    nearbyLocations: number;
    nearbySameFrequencyLocations: number;
    estimatedDriveMinutesToNearest?: number | null;
    estimatedDistanceMilesToNearest?: number | null;
    maxSuggestedRouteDriveMinutes: number;
    maxSuggestedRouteDistanceMiles: number;
    maxSuggestedRouteDayLocations: number;
    maxSuggestedRouteDayMachines: number;
  };
  suggestedAssignee?: {
    userId: string;
    confidence: string;
    reason: string;
  } | null;
};

export type BulkServiceScheduleLocation = {
  locationId: string;
  dayOfWeek: string;
  frequency: number;
  defaultAssigneeId?: string | null;
  frequencySuggestion?: ServiceCadenceSuggestion["frequencySuggestion"];
};

export type BulkServiceScheduleMetrics = {
  locationCount: number;
  machineCount: number;
  estimatedDriveMinutes?: number | null;
  estimatedDistanceMiles?: number | null;
  assigneeIds: Array<string | null>;
};

export type BulkServiceScheduleDay = {
  dayOfWeek: string;
  metrics: BulkServiceScheduleMetrics;
  warnings: string[];
};

export type BulkServiceScheduleResponse = {
  summary: BulkServiceScheduleMetrics;
  days: BulkServiceScheduleDay[];
};

export type BulkServiceScheduleOptimizeResponse =
  BulkServiceScheduleResponse & {
    locations: BulkServiceScheduleLocation[];
  };

export type BulkServiceScheduleChange = {
  locationId: string;
  expected: {
    frequency?: number | null;
    dayOfWeek?: string | null;
    defaultAssigneeId?: string | null;
  };
  next: {
    frequency: number;
    dayOfWeek: string;
    defaultAssigneeId?: string | null;
  };
  updateDefaultAssignee?: boolean;
};

export type BulkServiceScheduleApplyResponse = {
  updated: number;
  conflicts: Array<{
    locationId: string;
    message: string;
  }>;
};

const WEEKDAY_ENUM_PREFIX = "WeekDay.";
const normalizeSuggestionText = (value: unknown) =>
  typeof value === "string"
    ? value.split(WEEKDAY_ENUM_PREFIX).join("")
    : "";
const normalizeSuggestionFrequency = (value: unknown) => {
  const frequency = Number(value);
  return Number.isFinite(frequency) && frequency > 0 ? frequency : 7;
};
const normalizeFrequencySuggestion = (suggestion: any) => {
  const frequencySuggestion =
    suggestion?.frequency_suggestion ?? suggestion?.frequencySuggestion;
  if (!frequencySuggestion) return null;
  const metrics = frequencySuggestion.metrics || {};
  return {
    frequency: normalizeSuggestionFrequency(frequencySuggestion.frequency),
    usedHistory:
      frequencySuggestion.used_history ?? frequencySuggestion.usedHistory ?? false,
    confidence: frequencySuggestion.confidence || "none",
    reason: normalizeSuggestionText(frequencySuggestion.reason),
    warnings: (frequencySuggestion.warnings || []).map(normalizeSuggestionText),
    metrics: {
      collectionCount:
        metrics.collection_count ?? metrics.collectionCount ?? 0,
      intervalCount:
        metrics.interval_count ?? metrics.intervalCount ?? 0,
      grossCents: centsFromApi(metrics.gross_cents ?? metrics.grossCents),
      activeDays: Number(metrics.active_days ?? metrics.activeDays ?? 0),
      dailyGrossCents: optionalCentsFromApi(
        metrics.daily_gross_cents ?? metrics.dailyGrossCents,
      ) ?? null,
      revenueThresholdCents: optionalCentsFromApi(
        metrics.revenue_threshold_cents ?? metrics.revenueThresholdCents,
      ) ?? null,
    },
  };
};

export async function fetchServiceCadenceSuggestions(
  payload: Location
): Promise<ServiceCadenceSuggestion[]> {
  const cadence = normalizeServiceCadencePayload(payload.serviceCadence);
  const address = normalizeAddressPayload(payload.address);
  const body = {
    address,
    current_frequency: cadence?.frequency || 7,
    selected_assignee_id: payload.defaultAssigneeId || undefined,
    machine_ids: Array.from(new Set(payload.machines || [])),
    hours: normalizeHoursPayload(payload.hours) || {},
    location_id: payload.id,
  };
  const response = await fetchJson<{ suggestions?: any[] }>(
    `${locationBase}/service-cadence/suggestions`,
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify(body),
    },
    "Failed to optimize service cadence"
  );
  return (response.suggestions || []).map((suggestion: any) => ({
    cadence: {
      frequency: normalizeSuggestionFrequency(suggestion?.cadence?.frequency),
      dayOfWeek: normalizeSuggestionText(
        suggestion?.cadence?.day_of_week ?? suggestion?.cadence?.dayOfWeek,
      ),
    },
    confidence: suggestion?.confidence,
    isCurrent: suggestion?.is_current ?? suggestion?.isCurrent ?? false,
    score: suggestion?.score,
    reason: normalizeSuggestionText(suggestion?.reason),
    warnings: (suggestion?.warnings || []).map(normalizeSuggestionText),
    frequencySuggestion: normalizeFrequencySuggestion(suggestion),
    metrics: {
      sameDayLocations:
        suggestion?.metrics?.same_day_locations ?? suggestion?.metrics?.sameDayLocations ?? 0,
      sameDayMachines:
        suggestion?.metrics?.same_day_machines ?? suggestion?.metrics?.sameDayMachines ?? 0,
      dayLocationCountAfterAdd:
        suggestion?.metrics?.day_location_count_after_add ??
        suggestion?.metrics?.dayLocationCountAfterAdd ??
        0,
      dayMachineCountAfterAdd:
        suggestion?.metrics?.day_machine_count_after_add ??
        suggestion?.metrics?.dayMachineCountAfterAdd ??
        0,
      nearbyLocations:
        suggestion?.metrics?.nearby_locations ?? suggestion?.metrics?.nearbyLocations ?? 0,
      nearbySameFrequencyLocations:
        suggestion?.metrics?.nearby_same_frequency_locations ??
        suggestion?.metrics?.nearbySameFrequencyLocations ??
        0,
      estimatedDriveMinutesToNearest:
        suggestion?.metrics?.estimated_drive_minutes_to_nearest ??
        suggestion?.metrics?.estimatedDriveMinutesToNearest ??
        null,
      estimatedDistanceMilesToNearest:
        suggestion?.metrics?.estimated_distance_miles_to_nearest ??
        suggestion?.metrics?.estimatedDistanceMilesToNearest ??
        null,
      maxSuggestedRouteDriveMinutes:
        suggestion?.metrics?.max_suggested_route_drive_minutes ??
        suggestion?.metrics?.maxSuggestedRouteDriveMinutes ??
        0,
      maxSuggestedRouteDistanceMiles:
        suggestion?.metrics?.max_suggested_route_distance_miles ??
        suggestion?.metrics?.maxSuggestedRouteDistanceMiles ??
        0,
      maxSuggestedRouteDayLocations:
        suggestion?.metrics?.max_suggested_route_day_locations ??
        suggestion?.metrics?.maxSuggestedRouteDayLocations ??
        0,
      maxSuggestedRouteDayMachines:
        suggestion?.metrics?.max_suggested_route_day_machines ??
        suggestion?.metrics?.maxSuggestedRouteDayMachines ??
        0,
    },
    suggestedAssignee: suggestion?.suggested_assignee
      ? {
          userId: suggestion.suggested_assignee.user_id,
          confidence: suggestion.suggested_assignee.confidence,
          reason: normalizeSuggestionText(suggestion.suggested_assignee.reason),
        }
      : suggestion?.suggestedAssignee
        ? {
            ...suggestion.suggestedAssignee,
            reason: normalizeSuggestionText(suggestion.suggestedAssignee.reason),
          }
        : null,
  }));
}

const serviceSchedulePlanPayload = (locations: BulkServiceScheduleLocation[]) =>
  locations.map((location) => ({
    location_id: location.locationId,
    day_of_week: location.dayOfWeek,
    frequency: location.frequency,
    default_assignee_id: location.defaultAssigneeId ?? null,
  }));

const normalizeBulkScheduleLocation = (location: any): BulkServiceScheduleLocation => ({
  locationId: String(location?.location_id ?? location?.locationId ?? ""),
  dayOfWeek: normalizeSuggestionText(location?.day_of_week ?? location?.dayOfWeek),
  frequency: normalizeSuggestionFrequency(location?.frequency),
  defaultAssigneeId:
    location?.default_assignee_id ?? location?.defaultAssigneeId ?? null,
  frequencySuggestion: normalizeFrequencySuggestion(location),
});

const normalizeBulkScheduleMetrics = (metrics: any): BulkServiceScheduleMetrics => ({
  locationCount: metrics?.location_count ?? metrics?.locationCount ?? 0,
  machineCount: metrics?.machine_count ?? metrics?.machineCount ?? 0,
  estimatedDriveMinutes:
    metrics?.estimated_drive_minutes ?? metrics?.estimatedDriveMinutes ?? null,
  estimatedDistanceMiles:
    metrics?.estimated_distance_miles ?? metrics?.estimatedDistanceMiles ?? null,
  assigneeIds: metrics?.assignee_ids ?? metrics?.assigneeIds ?? [],
});

const normalizeBulkScheduleResponse = (response: any): BulkServiceScheduleResponse => ({
  summary: normalizeBulkScheduleMetrics(response?.summary || {}),
  days: (response?.days || []).map((day: any) => ({
    dayOfWeek: normalizeSuggestionText(day?.day_of_week ?? day?.dayOfWeek),
    metrics: normalizeBulkScheduleMetrics(day?.metrics || {}),
    warnings: (day?.warnings || []).map(normalizeSuggestionText),
  })),
});

export async function fetchBulkServiceScheduleMetrics(
  locations: BulkServiceScheduleLocation[]
): Promise<BulkServiceScheduleResponse> {
  const response = await fetchJson<any>(
    `${locationBase}/service-cadence/bulk-metrics`,
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify({ locations: serviceSchedulePlanPayload(locations) }),
    },
    "Failed to calculate scheduling metrics"
  );
  return normalizeBulkScheduleResponse(response);
}

export async function fetchBulkServiceScheduleOptimize(
  locations?: BulkServiceScheduleLocation[],
  serviceDays?: string[],
): Promise<BulkServiceScheduleOptimizeResponse> {
  const response = await fetchJson<any>(
    `${locationBase}/service-cadence/bulk-optimize`,
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify({
        locations: locations ? serviceSchedulePlanPayload(locations) : [],
        ...(serviceDays ? { service_days: serviceDays } : {}),
      }),
    },
    "Failed to optimize scheduling"
  );
  const metrics = normalizeBulkScheduleResponse(response);
  return {
    ...metrics,
    locations: (response?.locations || []).map(normalizeBulkScheduleLocation),
  };
}

export async function applyBulkServiceScheduleChanges(
  changes: BulkServiceScheduleChange[]
): Promise<BulkServiceScheduleApplyResponse> {
  const response = await fetchJson<any>(
    `${locationBase}/service-cadence/bulk-apply`,
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify({
        changes: changes.map((change) => ({
          location_id: change.locationId,
          expected: {
            frequency: change.expected.frequency ?? null,
            day_of_week: change.expected.dayOfWeek ?? null,
            default_assignee_id: change.expected.defaultAssigneeId ?? null,
          },
          next: {
            frequency: change.next.frequency,
            day_of_week: change.next.dayOfWeek,
            default_assignee_id: change.next.defaultAssigneeId ?? null,
          },
          update_default_assignee: change.updateDefaultAssignee ?? false,
        })),
      }),
    },
    "Failed to apply scheduling changes"
  );
  return {
    updated: response?.updated ?? 0,
    conflicts: (response?.conflicts || []).map((conflict: any) => ({
      locationId: String(conflict?.location_id ?? conflict?.locationId ?? ""),
      message: normalizeSuggestionText(conflict?.message),
    })),
  };
}

export async function editLocation(payload: Location, options?: { includeAddress?: boolean }): Promise<Location> {
  if (!payload.id) throw new Error("Location id is required to update");
  const body = buildLocationPayload(payload, {
    includeAddress: options?.includeAddress === true,
    includeMachines: false, // backend union() expects a queryset; omit to avoid errors unless a dedicated assignment flow is used
  });

  const updated = await fetchJson<Location>(
    `${locationBase}/edit?id=${payload.id}`,
    {
      method: "PUT",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify({ ...body, expectedUpdatedAt: payload.updatedAt }),
    },
    "Failed to update location"
  );
  return normalizeLocationResponse(updated);
}

export async function uploadLocationPhoto(
  locationId: string,
  file: File
): Promise<PhotoAsset> {
  const form = new FormData();
  form.append("file", file);
  const token = getCsrfToken() || (await setCsrfToken());
  const headers = await defaultFetchHeaders();
  delete (headers as Record<string, string>)["Content-Type"];
  const res = await fetch(
    buildUrl(`${locationBase}/asset/photo/add`, { location_id: locationId }),
    {
      method: "POST",
      credentials: "include",
      headers: {
        ...headers,
        "X-CSRFToken": token,
      },
      body: form,
    }
  );
  if (!res.ok) {
    throw new Error(await errorMessageFromResponse(res, "Failed to upload photo"));
  }
  const asset = normalizePhotoAsset(
    await jsonFromResponse(res, "Failed to upload photo")
  );
  if (!asset) throw new Error("Failed to upload photo");
  return asset;
}

export async function copyLocationPhotoFromMachine(
  locationId: string,
  machineId: string,
  assetId: string,
): Promise<PhotoAsset> {
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetch(
    buildUrl(`${locationBase}/asset/photo/copy-from-machine`, {
      location_id: locationId,
      machine_id: machineId,
      asset_id: assetId,
    }),
    { method: "POST", credentials: "include", headers: { ...(await defaultFetchHeaders()), "X-CSRFToken": token } },
  );
  if (!res.ok) throw new Error(await errorMessageFromResponse(res, "Failed to copy machine photo"));
  const asset = normalizePhotoAsset(await jsonFromResponse(res, "Failed to copy machine photo"));
  if (!asset) throw new Error("Failed to copy machine photo");
  return asset;
}

export async function deleteLocationAsset(assetId: string): Promise<void> {
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetch(buildUrl(`${locationBase}/asset/delete`, { id: assetId }), {
    method: "DELETE",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "X-CSRFToken": token,
    },
  });
  if (!res.ok) {
    throw new Error(await errorMessageFromResponse(res, "Failed to remove photo"));
  }
}

export async function editLocationAddress(
  locationId: string,
  address: Address
): Promise<void> {
  if (!locationId) throw new Error("Location id is required to update");
  const body = {
    street: address.street || "",
    city: address.city || "",
    state: address.state || "",
    country: address.country || "",
    zip: address.zip ? String(address.zip) : "",
  };

  await fetchJson(
    `${locationBase}/address/edit?id=${locationId}`,
    {
      method: "PUT",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify(body),
    },
    "Failed to update address"
  );
}

// discontinue and delete
export async function deleteLocation(id: string): Promise<void> {
  suppressRealtimeEvent("location.changed", id);
  await fetchJson(
    buildUrl(`${locationBase}/delete`, { id }),
    {
      method: "DELETE",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to delete location"
  );
}

export async function discontinueLocation(id: string): Promise<Location> {
  suppressRealtimeEvent("location.changed", id);
  const response = await fetchJson(
    buildUrl(`${locationBase}/discontinue`, { id }),
    {
      method: "PUT",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to discontinue location"
  );
  return normalizeLocationResponse(response);
}

export async function fetchLocationStatusImpact(id: string): Promise<LocationStatusImpact> {
  const response: any = await fetchJson(
    buildUrl(`${locationBase}/status-impact`, { id }),
    { credentials: "include", headers: await defaultFetchHeaders() },
    "Failed to load location status impact",
  );
  const normalizeTask = (task: any) => ({
    id: task.id,
    machineId: task.machine_id ?? task.machineId,
    machineName: task.machine_name ?? task.machineName,
    type: task.type,
    status: task.status,
    scheduledFor: task.scheduled_for ?? task.scheduledFor ?? null,
    startedAt: task.started_at ?? task.startedAt ?? null,
  });
  return {
    assignedMachines: (response.assigned_machines ?? response.assignedMachines ?? []).map(
      (machine: any) => ({ id: machine.id, name: machine.name }),
    ),
    inProgressTasks: (response.in_progress_tasks ?? response.inProgressTasks ?? []).map(normalizeTask),
    tasksToDelete: (response.tasks_to_delete ?? response.tasksToDelete ?? []).map(normalizeTask),
    canPutOnHold: Boolean(response.can_put_on_hold ?? response.canPutOnHold),
  };
}

export async function putLocationOnHold(
  id: string,
  unassignMachines: boolean,
  machineIds: string[] = [],
): Promise<Location> {
  suppressRealtimeEvent("location.changed", id);
  machineIds.forEach((machineId) => suppressRealtimeEvent("machine.changed", machineId));
  const response = await fetchJson(
    buildUrl(`${locationBase}/hold`, { id }),
    {
      method: "PUT",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify({ unassign_machines: unassignMachines }),
    },
    "Failed to put location on hold",
  );
  return normalizeLocationResponse(response);
}

export async function activateLocation(id: string): Promise<Location> {
  suppressRealtimeEvent("location.changed", id);
  const response = await fetchJson(
    buildUrl(`${locationBase}/activate`, { id }),
    {
      method: "PUT",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to reactivate location",
  );
  return normalizeLocationResponse(response);
}

export async function fetchLocationServiceMetrics(params: {
  scheduledFor?: string;
  date?: string;
  start?: string;
  end?: string;
  force?: boolean;
}): Promise<RoutePlannerLocationMetric[]> {
  const scheduledFor = params.scheduledFor ?? params.date;
  const end = params.end;
  const cacheKey = getMetricsCacheKey({
    scheduledFor,
    start: params.start,
    end,
  });
  if (!params.force) {
    const cached = metricsCache.get(cacheKey, METRICS_CACHE_TTL_MS);
    if (cached) return cached;
    const inflight = metricsInFlight.get(cacheKey);
    if (inflight) return inflight;
  }
  const request = (async () => {
    const list = await fetchJson<any[]>(
      buildUrl(`${locationBase}/service/metrics`, {
        scheduled_for: scheduledFor ?? undefined,
        start: params.start ?? undefined,
        end,
      }),
      {
        method: "GET",
        credentials: "include",
        headers : await defaultFetchHeaders(),
      },
      "Failed to fetch location service metrics"
    );
    const metrics = (list || []).map((metric) => ({
      locationId: metric.id,
      locationName: metric.name,
      grossCents: metric.grossCents ?? metric.gross_cents ?? 0,
      commissionCents: metric.commissionCents ?? metric.commission_cents ?? 0,
      netCents: metric.netCents ?? metric.net_cents ?? 0,
      machines: (metric.machines || []).map((machine: any) => ({
        machineId: machine.id,
        machineName: machine.name,
        grossCents: machine.grossCents ?? machine.gross_cents ?? 0,
        grossCashCents: machine.grossCashCents ?? machine.gross_cash_cents ?? 0,
        grossCardCents: machine.grossCardCents ?? machine.gross_card_cents ?? 0,
        lastServiceAt: machine.last_service_at ?? null,
        daysSinceLastService: machine.days_since_last_service ?? null,
        cardTransactionCount:
          machine.cardTransactionCount ?? machine.card_transaction_count ?? 0,
        cardTransactionSurchargeTotalCents:
          machine.cardTransactionSurchargeTotalCents ??
          machine.card_transaction_surcharge_total_cents ??
          0,
        grossAfterCardTransactionSurchargeCents:
          machine.grossAfterCardTransactionSurchargeCents ??
          machine.gross_after_card_transaction_surcharge_cents ??
          0,
      })),
    }));
    metricsCache.set(cacheKey, metrics);
    return metrics;
  })();
  if (!params.force) {
    metricsInFlight.set(cacheKey, request);
  }
  try {
    return await request;
  } finally {
    if (!params.force) {
      metricsInFlight.delete(cacheKey);
    }
  }
}

const normalizeServiceCadencePreview = (row: any): ServiceCadenceTaskReschedulePreview => ({
  taskId: row.taskId ?? row.task_id ?? "",
  machineId: row.machineId ?? row.machine_id ?? "",
  machineName: row.machineName ?? row.machine_name ?? "",
  currentScheduledFor: row.currentScheduledFor ?? row.current_scheduled_for ?? "",
  newScheduledFor: row.newScheduledFor ?? row.new_scheduled_for ?? "",
});

export async function previewServiceCadenceTaskReschedule(
  locationId: string,
  serviceCadence: Location["serviceCadence"],
): Promise<ServiceCadenceTaskReschedulePreview[]> {
  if (!locationId) throw new Error("Location id is required");
  const body = normalizeServiceCadencePayload(serviceCadence);
  if (!body) return [];
  const rows = await fetchJson<any[]>(
    buildUrl(`${locationBase}/service/cadence/reschedule/preview`, { id: locationId }),
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify(body),
    },
    "Failed to preview service cadence task updates",
  );
  return (rows || []).map(normalizeServiceCadencePreview);
}

export async function rescheduleServiceCadenceTasks(
  locationId: string,
): Promise<ServiceCadenceTaskReschedulePreview[]> {
  if (!locationId) throw new Error("Location id is required");
  const rows = await fetchJson<any[]>(
    buildUrl(`${locationBase}/service/cadence/reschedule`, { id: locationId }),
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
    },
    "Failed to update service cadence tasks",
  );
  return (rows || []).map(normalizeServiceCadencePreview);
}

export type QuickRescheduleOption = {
  kind: "today" | "nextCadence" | "followingCadence";
  scheduledFor: string;
};

const QUICK_RESCHEDULE_KINDS = new Set<QuickRescheduleOption["kind"]>([
  "today",
  "nextCadence",
  "followingCadence",
]);

export async function fetchQuickRescheduleOptions(
  locationId: string,
  scheduledFor: string,
  machineId?: string | null,
): Promise<QuickRescheduleOption[]> {
  if (!locationId) return [];
  const response = await fetchJson<any>(
    buildUrl(`${locationBase}/service/cadence/next`, {
      id: locationId,
      scheduledFor,
      machineId: machineId || undefined,
    }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch quick reschedule dates",
  );
  const rawOptions = response?.quickRescheduleOptions ?? response?.quick_reschedule_options;
  if (!Array.isArray(rawOptions)) {
    const today = toLocalISODate();
    const nextDate = response?.nextScheduledFor ?? response?.next_scheduled_for;
    return [
      ...(scheduledFor !== today ? [{ kind: "today" as const, scheduledFor: today }] : []),
      ...(nextDate && nextDate !== scheduledFor && nextDate !== today
        ? [{ kind: "nextCadence" as const, scheduledFor: nextDate }]
        : []),
    ];
  }
  return rawOptions
    .map((option: any) => ({
      kind: option.kind,
      scheduledFor: option.scheduledFor ?? option.scheduled_for,
    }))
    .filter((option: QuickRescheduleOption) =>
      QUICK_RESCHEDULE_KINDS.has(option.kind) && Boolean(option.scheduledFor)
    );
}

export async function fetchRoadDistance(params: {
  startLat: number;
  startLng: number;
  endLat: number;
  endLng: number;
}): Promise<number | null> {
  const data = await fetchJson<{ distance_miles: number | null }>(
    buildUrl(`${locationBase}/distance/road`, {
      start_lat: params.startLat,
      start_lng: params.startLng,
      end_lat: params.endLat,
      end_lng: params.endLng,
    }),
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch road distance"
  );
  return data?.distance_miles ?? null;
}

export async function fetchRoadRouteGeometry(params: {
  points: Array<{ lat: number; lng: number }>;
}): Promise<{ distanceMiles: number | null; geometry: any | null }> {
  const res = await fetch(`${locationBase}/route/geometry`, {
    method: "POST",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({ points: params.points }),
  });
  if (!res.ok) {
    throw new Error(await errorMessageFromResponse(res, "Failed to fetch route"));
  }
  const data = await jsonFromResponse<{
    distance_miles: number | null;
    geometry: any | null;
  }>(res, "Failed to fetch route");
  return { distanceMiles: data.distance_miles ?? null, geometry: data.geometry ?? null };
}

export async function fetchDynamicSchedulingProgress(id: string, signal?: AbortSignal): Promise<import("../../types").DynamicSchedulingProgress> {
  return fetchJson(buildUrl(`${locationBase}/dynamic-scheduling/progress`, { id }), {
    credentials: "include", headers: defaultReadHeaders(), signal,
  }, "Failed to load dynamic scheduling progress");
}
